import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import { ICommentAPI } from '../index';
import NamePill from '../../../molecules/NamePill';
import CommentCard from './comment/indext';
import { Typography } from '../../../molecules/Typography';
import { Button } from '../../../atoms/Button';

interface IMobileCarouselProps {
  comments: ICommentAPI[];
  totalComments: number;
  onLike: (id: string) => void;
  onReply: (text: string, id: string) => void;
  onDelete: (comment: any) => void;
  onUpdate: (comment: any, newText: string) => void;
  deleteLoading: boolean;
  onOpenDrawer: () => void;
}

export default function MobileCommentCarousel({
  comments,
  totalComments,
  onLike,
  onReply,
  onDelete,
  onUpdate,
  deleteLoading,
  onOpenDrawer,
}: IMobileCarouselProps) {
  const visibleComments = comments.slice(0, 5);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );

  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onInit, onSelect]);

  if (visibleComments.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-md'>
      <div className='flex w-full items-center justify-between gap-md'>
        <button
          className='flex w-fit items-center gap-xs'
          onClick={onOpenDrawer}
        >
          <Typography size='small'>{totalComments} comentários</Typography>
        </button>
        <Button
          LeftIcon='chevron_right'
          iconType='material'
          variant='text'
          size='small'
          onClick={onOpenDrawer}

        >
          <Typography size='small' className='text-brand-color-tertiary-default'>
            Ver todas as mensagens
          </Typography>
        </Button>
      </div>

      <div className='overflow-hidden' ref={emblaRef}>
        <div className='flex items-start gap-4'>
          {visibleComments.map((comment, index) => (
            <div
              key={comment._id || index}
              className='min-w-0 [&_.whitespace-pre-wrap]:line-clamp-3'
              style={{ flex: '0 0 100%' }}
            >
              <div className='flex min-w-0 flex-row gap-3'>
                <div className='flex min-w-0 flex-1 flex-col gap-md overflow-hidden'>
                  <CommentCard
                    comment={comment}
                    disableReply={!comment._id}
                    onSubmit={(text: string) => onReply(text, comment._id)}
                    onLike={() => onLike(comment._id)}
                    isModal={false}
                    onDelete={() => onDelete(comment)}
                    deleteLoading={deleteLoading}
                    onUpdate={(value: string) => onUpdate(comment, value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {scrollSnaps.length > 1 && (
        <div className='flex items-center justify-center gap-3'>
          <Button
            onlyIcon
            LeftIcon='chevron_left'
            iconType='material'
            variant='text'
            size='small'
            className='p-0'
            disabled={prevBtnDisabled}
            onClick={scrollPrev}
          />
          <div className='flex items-center gap-2'>
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={`h-2 w-2 rounded-circular transition-all ${
                  index === selectedIndex
                    ? 'bg-color-brand-primary'
                    : 'bg-brand-color-primary-lighter'
                }`}
                onClick={() => scrollTo(index)}
                aria-label={`Comentário ${index + 1}`}
              />
            ))}
          </div>
          <Button
            onlyIcon
            LeftIcon='chevron_right'
            iconType='material'
            variant='text'
            size='small'
            className='p-0'
            disabled={nextBtnDisabled}
            onClick={scrollNext}
          />
        </div>
      )}
    </div>
  );
}
